import { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import type { F } from 'ts-toolbelt';

import { getDocumentName } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../utils/back-errors.util';
import { MissionPage_OneMission_QueryDocument } from '../../../pages/Mission/hooks/useFindOneMissionQuery.hook';
import type { BackError } from '../../../utils/back-errors.util';

import { memoForm2ApiMapper } from '../../MemoBeforeSubmit/mappers/memo.form2api.mapper';
import type { FolderBusinessEntityStateFormValues } from '../../FolderBusinessEntityState/types/values.type';
import type { UseUpdateFolderBusinessEntityStateSubmit } from '../../FolderBusinessEntityState/types/use-submit.type';

import { useUpdateMissionStateMutation } from './useUpdateMissionStateMutation.hook';
import type { MissionStateForm_UpdateMissionState_Result } from './useUpdateMissionStateMutation.hook';

export const useUpdateMissionStateSubmitFactory = (
  missionId: Id,
): UseUpdateFolderBusinessEntityStateSubmit =>
  function useUpdateMissionStateSubmit({
    memos,
    onSuccess: handleSuccess,
  }: F.Parameters<UseUpdateFolderBusinessEntityStateSubmit>[0]) {
    const [formError, setFormError] = useState<readonly string[] | null>(null);

    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    const { mutate: updateMissionStateMutation } =
      useUpdateMissionStateMutation();

    const handleSubmit = useCallback(
      (
        values: Readonly<FolderBusinessEntityStateFormValues>,
        {
          setSubmitting,
        }: Readonly<{ setSubmitting: (isSubmitting: boolean) => void }>,
      ) => {
        setFormError(null);

        const onSuccess = (
          _: Readonly<MissionStateForm_UpdateMissionState_Result>,
        ) => {
          enqueueSnackbar('Le statut de votre mission a bien été mis à jour.');

          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(MissionPage_OneMission_QueryDocument),
              {
                id: missionId,
              },
            ],
          });

          handleSuccess?.();

          setSubmitting(false);
        };

        const onError = (error: unknown) => {
          setFormError(userFriendlyErrors(error as BackError));

          setSubmitting(false);
        };

        updateMissionStateMutation(
          {
            missionId,
            ...values,
            versionData: memoForm2ApiMapper(memos),
          },
          {
            onSuccess,
            onError,
          },
        );
      },
      [
        enqueueSnackbar,
        handleSuccess,
        memos,
        queryClient,
        updateMissionStateMutation,
      ],
    );

    return {
      formError,
      handleSubmit,
    };
  };
